import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import {
  Auftrag,
  AuftragCreate,
  AUFTRAG_STATUS_LABELS,
  AUFTRAG_STATUS_ORDER,
  AuftragStatus,
  TakedaEinsatzort,
  TakedaProjektnummer,
  WarehouseRef
} from '../../../Models/stammkunde.model';

export interface AuftragFormData {
  customerId: number;
  auftrag?: Auftrag;
  linkedWarehouses: WarehouseRef[];
  /** true = Takeda-Kunde: Einsatzort und Projektnummer kommen aus Stammdaten. */
  isTakeda?: boolean;
}

/** Dialog zum Anlegen/Bearbeiten eines Stammkunden-Auftrags. */
@Component({
  selector: 'app-auftrag-form',
  templateUrl: './auftrag-form.component.html',
  styleUrls: ['./auftrag-form.component.css', '../stammkunden-theme.css']
})
export class AuftragFormComponent {
  isEdit: boolean;
  saving = false;

  title = '';
  description = '';
  orderNumber = '';
  location = '';
  receivedDate: Date | null = null;
  startDate: Date | null = null;
  dueDate: Date | null = null;
  projektNummer: string | null = null;
  primaryWarehouseId: number | null = null;
  status: AuftragStatus = 'New';

  readonly statusOptions = AUFTRAG_STATUS_ORDER;
  readonly statusLabels = AUFTRAG_STATUS_LABELS;

  // Takeda-Stammdaten für die Auswahlfelder. Bleiben leer, wenn der Kunde kein
  // Takeda-Kunde ist oder noch nichts gepflegt wurde – dann greift der Rückfall
  // auf ein freies Textfeld (siehe Vorlage).
  einsatzorte: TakedaEinsatzort[] = [];
  projektnummern: TakedaProjektnummer[] = [];
  stammdatenLoading = false;

  constructor(
    public dialogRef: MatDialogRef<AuftragFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AuftragFormData,
    private stammkundeService: StammkundeService,
    private snackBar: MatSnackBar
  ) {
    this.isEdit = !!data.auftrag;
    const a = data.auftrag;
    if (a) {
      this.title = a.title;
      this.description = a.description ?? '';
      this.orderNumber = a.orderNumber ?? '';
      this.location = a.location ?? '';
      this.receivedDate = this.toDate(a.receivedDate);
      this.startDate = this.toDate(a.startDate);
      this.dueDate = this.toDate(a.dueDate);
      this.projektNummer = a.projektNummer ?? null;
      this.primaryWarehouseId = a.primaryWarehouseId ?? null;
      this.status = a.status;
    }

    if (this.istTakeda) {
      this.ladeStammdaten();
    }
  }

  /** Auswahlfelder nur dort, wo auch Stammdaten gepflegt werden können. */
  get istTakeda(): boolean {
    return this.data.isTakeda === true || this.data.auftrag?.isTakeda === true;
  }

  /**
   * Sind Einsatzort-Stammdaten vorhanden? Wenn nicht, bleibt das Feld ein freies
   * Textfeld – sonst könnte man ohne gepflegte Stammdaten keinen Ort erfassen.
   */
  get hatEinsatzortAuswahl(): boolean {
    return this.einsatzorte.length > 0;
  }

  get hatProjektnummerAuswahl(): boolean {
    return this.projektnummern.length > 0;
  }

  /**
   * Der gespeicherte Ort eines Altauftrags kann ein Wert sein, den es in den
   * Stammdaten (noch) nicht gibt. Er wird der Auswahl hinzugefügt, damit das
   * Bearbeiten ihn nicht stillschweigend verwirft.
   */
  get einsatzortNamen(): string[] {
    const namen = this.einsatzorte.map(o => o.name);
    const aktuell = this.location?.trim();
    if (aktuell && !namen.includes(aktuell)) {
      namen.unshift(aktuell);
    }
    return namen;
  }

  private ladeStammdaten(): void {
    this.stammdatenLoading = true;

    // Nur aktive Einträge: deaktivierte sollen nicht neu vergeben werden.
    forkJoin({
      orte: this.stammkundeService
        .getTakedaEinsatzorte(this.data.customerId, true)
        .pipe(catchError(() => of<TakedaEinsatzort[]>([]))),
      nummern: this.stammkundeService
        .getTakedaProjektnummern(this.data.customerId, true)
        .pipe(catchError(() => of<TakedaProjektnummer[]>([])))
    }).subscribe(result => {
      this.einsatzorte = result.orte ?? [];
      this.projektnummern = result.nummern ?? [];
      this.stammdatenLoading = false;

      // Eine Projektnummer, die inzwischen deaktiviert wurde, würde der Server
      // ablehnen. Sie bleibt sichtbar, damit der Benutzer sie bewusst ersetzt.
      const aktuell = this.projektNummer?.trim();
      if (aktuell && !this.projektnummern.some(p => p.nummer === aktuell)) {
        this.projektnummern = [
          { id: 0, customerId: this.data.customerId, nummer: aktuell, isActive: false },
          ...this.projektnummern
        ];
      }
    });
  }

  save(): void {
    if (!this.title.trim()) return;
    this.saving = true;

    const payload: AuftragCreate = {
      customerId: this.data.customerId,
      title: this.title.trim(),
      description: this.description.trim() || undefined,
      orderNumber: this.orderNumber.trim() || undefined,
      location: this.location.trim() || undefined,
      projektNummer: this.projektNummer?.trim() || null,
      receivedDate: this.toDateString(this.receivedDate),
      startDate: this.toDateString(this.startDate),
      dueDate: this.toDateString(this.dueDate),
      primaryWarehouseId: this.primaryWarehouseId,
      status: this.isEdit ? this.status : undefined
    };

    const request$ = this.isEdit
      ? this.stammkundeService.updateAuftrag(this.data.auftrag!.id, payload)
      : this.stammkundeService.createAuftrag(payload);

    request$.subscribe({
      next: result => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: err => {
        this.saving = false;
        const message = err?.error?.message || 'Fehler beim Speichern des Auftrags.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private toDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);

    return isNaN(date.getTime()) ? null : date;
  }

  private toDateString(value: Date | null): string | null {
    if (!value) {
      return null;
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
